import React, { useEffect, useState } from 'react';
import AdminDashboard from 'layouts/AdminDashboard';
import { Alert, Box, Breadcrumbs } from '@mui/material';
import { getItem, updateItem } from './ItemService';
import { extractErrorMessage } from 'utils/extractErrorMessage';
import { useParams } from 'react-router-dom';
import Container from 'components/Container';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import Button from '@mui/material/Button';

import { format } from 'date-fns';
import { toDate } from 'date-fns-tz';

import ItemChildrenList from './ItemChildrenList';
// import SelectedItemList from './SelectedItemList';
import ItemQuestionList from './ItemQuestionList';
import ItemDocumentList from './ItemDocumentList';
import ItemAttachmentList from './ItemAttachmentList';
import ItemAssignmentList from './ItemAssignmentList';
const ItemDetail = (): JSX.Element => {
  const [name, setName] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [description, setDescription] = useState('');
  const [versionNumber, setVersionNumber] = useState('');
  const [buildNumber, setBuildNumber] = useState('');
  const [appCode, setAppCode] = useState('');
  const [addonCode, setAddonCode] = useState('');
  const [status, setStatus] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const { itemid } = useParams();
  const { subscriptionid } = useParams();

  const [parentLevel, setLevel] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (itemid) {
      console.log('Itemid exists: ', itemid);
      getItem(itemid)
        .then((response) => {
          console.log(response.data);
          setName(response.data.name);
          setSectionId(response.data.section.id);
          setVersionNumber(response.data.versionNumber);
          setBuildNumber(response.data.buildNumber);
          setAppCode(response.data.appCode);
          setAddonCode(response.data.addonCode ?? '');
          setStatus(response.data.status);
          setStartDate(response.data.startDate);
          setEndDate(response.data.endDate);
          setLevel(response.data.section.level);
        })
        .catch((error) => {
          setErrorMessage(extractErrorMessage(error));
        });
    }
  }, [subscriptionid, itemid]);

  const handleSave = () => {
    if (itemid) {
      updateItem(itemid, { name, description, appCode, addonCode, status })
        .then(() => console.log('Item saved'))
        .catch((error) => setErrorMessage(extractErrorMessage(error)));
    }
  };

  const timeZone = 'Europe/London';
  const formatString = 'dd-MM-yyyy HH:mm:ss';

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const zonedDate = toDate(date, { timeZone });
    return format(zonedDate, formatString);
  };

  return (
    <Container>
      <AdminDashboard>
        {errorMessage && (
          <Alert
            severity="error"
            onClose={() => setErrorMessage('')}
            sx={{ mb: 2 }}
          >
            {errorMessage}
          </Alert>
        )}
        <Box marginTop={2} marginBottom={4}>
          <Breadcrumbs aria-label="breadcrumb">
            <Link underline="hover" color="inherit" href="/">
              Your Items
            </Link>
            <Link
              underline="hover"
              color="inherit"
              href={`/dashboard/dashboard/items/section/${sectionId}`}
            >
              Items
            </Link>
            <Typography color="text.primary">Item Detail</Typography>
          </Breadcrumbs>
        </Box>
        <Box display="flex" justifyContent="space-between">
          <Box sx={{ width: '65%' }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              marginBottom={4}
            >
              <Typography variant="h4">Item Detail</Typography>
            </Box>

            <Box>
              <form>
                <Grid container spacing={4}>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant={'subtitle2'}
                      sx={{ marginBottom: 2 }}
                      fontWeight={700}
                    >
                      Name
                    </Typography>
                    <TextField
                      variant="outlined"
                      name={'name'}
                      fullWidth
                      value={name}
                      onChange={(event) => setName(event.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant={'subtitle2'}
                      sx={{ marginBottom: 2 }}
                      fontWeight={700}
                    >
                      Section ID
                    </Typography>
                    <TextField
                      variant="outlined"
                      name={'sectionId'}
                      fullWidth
                      value={sectionId}
                      onChange={(event) => setSectionId(event.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant={'subtitle2'}
                      sx={{ marginBottom: 2 }}
                      fontWeight={700}
                    >
                      Description
                    </Typography>
                    <TextField
                      variant="outlined"
                      name={'description'}
                      fullWidth
                      value={description}
                      onChange={(event) => setDescription(event.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant={'subtitle2'}
                      sx={{ marginBottom: 2 }}
                      fontWeight={700}
                    >
                      Version Number
                    </Typography>
                    <TextField
                      variant="outlined"
                      name={'versionNumber'}
                      fullWidth
                      value={versionNumber}
                      onChange={(event) => setVersionNumber(event.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant={'subtitle2'}
                      sx={{ marginBottom: 2 }}
                      fontWeight={700}
                    >
                      Level
                    </Typography>
                    <TextField
                      variant="outlined"
                      name={'parentLevel'}
                      fullWidth
                      value={parentLevel}
                      onChange={(event) => setLevel(Number(event.target.value))}
                      type="number" // Ensures the input is numerical
                      InputProps={{
                        readOnly: true,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant={'subtitle2'}
                      sx={{ marginBottom: 2 }}
                      fontWeight={700}
                    >
                      Build Number
                    </Typography>
                    <TextField
                      variant="outlined"
                      name={'buildNumber'}
                      fullWidth
                      value={buildNumber}
                      onChange={(event) => setBuildNumber(event.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant={'subtitle2'}
                      sx={{ marginBottom: 2 }}
                      fontWeight={700}
                    >
                      App Code
                    </Typography>
                    <TextField
                      variant="outlined"
                      name={'appCode'}
                      fullWidth
                      value={appCode}
                      onChange={(event) => setAppCode(event.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant={'subtitle2'}
                      sx={{ marginBottom: 2 }}
                      fontWeight={700}
                    >
                      Add-on Code
                    </Typography>
                    <TextField
                      variant="outlined"
                      name={'addonCode'}
                      fullWidth
                      value={addonCode}
                      onChange={(event) => setAddonCode(event.target.value)}
                      placeholder="e.g. MORTGAGE_PREPARATION"
                      helperText="Leave blank for item-defined programme. System codes: FINANCE, DOCUMENT_COACH, MORTGAGE_PREPARATION, TAX_PREPARATION, HEALTH_RECORDS, MEDICATION_TRACKING, ESTATE_PLANNING, CONTRACTS"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant={'subtitle2'}
                      sx={{ marginBottom: 2 }}
                      fontWeight={700}
                    >
                      Status
                    </Typography>
                    <TextField
                      variant="outlined"
                      name={'status'}
                      fullWidth
                      value={status}
                      onChange={(event) => setStatus(event.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant={'subtitle2'}
                      sx={{ marginBottom: 2 }}
                      fontWeight={700}
                    >
                      Start Date
                    </Typography>
                    <TextField
                      variant="outlined"
                      name={'startDate'}
                      fullWidth
                      value={formatDate(startDate)}
                      onChange={(event) => setStartDate(event.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant={'subtitle2'}
                      sx={{ marginBottom: 2 }}
                      fontWeight={700}
                    >
                      End Date
                    </Typography>
                    <TextField
                      variant="outlined"
                      name={'endDate'}
                      fullWidth
                      value={formatDate(endDate)}
                      onChange={(event) => setEndDate(event.target.value)}
                    />
                  </Grid>
                  <Grid item container xs={12}>
                    <Box
                      display="flex"
                      flexDirection={{ xs: 'column', sm: 'row' }}
                      alignItems={{ xs: 'stretched', sm: 'center' }}
                      justifyContent={'flex-end'}
                      width={1}
                      marginBottom={4}
                    >
                      <Button variant="contained" onClick={handleSave}>
                        Save
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </form>
            </Box>
          </Box>
          <Box sx={{ width: '30%', marginLeft: '30px' }}>
            <ItemAssignmentList />
          </Box>
        </Box>
        <Box>
          <ItemQuestionList itemId={itemid} />
          <ItemDocumentList itemId={itemid} />
          <ItemAttachmentList itemId={itemid} />
          <ItemChildrenList parentId={itemid} />
        </Box>
      </AdminDashboard>
    </Container>
  );
};

export default ItemDetail;
